import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Breadcrumb from '@/components/common/Breadcrumb';

const ArticleDetail = () => {

  const parseFrontMatter = (text: string) => {
    const lines = text.split('\n');
    let inFrontMatter = false;
    const data: any = {};
    let contentStartIndex = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      if (line.trim() === '---') {
        if (!inFrontMatter) {
          inFrontMatter = true;
        } else {
          contentStartIndex = i + 1;
          break;
        }
      } else if (inFrontMatter) {
        const colonIndex = line.indexOf(':');
        if (colonIndex !== -1) {
          const key = line.slice(0, colonIndex).trim();
          let value = line.slice(colonIndex + 1).trim();

          if (value.startsWith('[') && value.endsWith(']')) {
            data[key] = value
              .slice(1, -1)
              .split(',')
              .map((item: string) => item.trim().replace(/^"/, '').replace(/"$/, ''));
          } else {
            value = value.replace(/^"/, '').replace(/"$/, '');
            if (value === 'true') {
              data[key] = true;
            } else if (value === 'false') {
              data[key] = false;
            } else {
              data[key] = value;
            }
          }
        }
      }
    }

    const content = lines.slice(contentStartIndex).join('\n').trim();

    return { data, content };
  };
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [metaData, setMetaData] = useState<any>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const loadArticle = async () => {
      const articlesPath = '/data/guides/articles/';
      const metaPath = '/data/guides/articles/meta.json';

      try {
        const [metaResponse] = await Promise.all([
          fetch(metaPath).then(res => res.text())
        ]);

        const parsedMeta = JSON.parse(metaResponse) as any;
        setMetaData(parsedMeta);

        let foundArticle = null;

        if (parsedMeta && Array.isArray(parsedMeta.categories)) {
          for (const category of parsedMeta.categories) {
            if (category && Array.isArray(category.articleIds)) {
              for (const articleId of category.articleIds) {
                const response = await fetch(`${articlesPath}${articleId}.md`);
                const text = await response.text();
                const { data, content } = parseFrontMatter(text);

                if (data && data.slug === slug) {
                  foundArticle = {
                    id: articleId,
                    title: data.title,
                    slug: data.slug,
                    publishedAt: data.publishedAt,
                    readTime: data.readTime,
                    featured: data.featured || false,
                    category: data.category,
                    tags: data.tags || [],
                    content
                  };
                  break;
                }
              }
            }
          }
        }

        setArticle(foundArticle);
      } catch (error) {
        console.error('Error loading article:', error);
        setLoadError(error instanceof Error ? error.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    loadArticle();
  }, [slug]);

   const category = article && metaData && Array.isArray(metaData.categories)
     ? metaData.categories.find((c: any) => c.id === article?.category)
     : null;
 
  if (loading) {
    return (
      <div className="container-custom flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="container-custom py-20">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Error Loading Article</h1>
          <p className="text-gray-600 mb-4">
            {loadError}
          </p>
          <Link to="/guides" className="btn-primary">
            Back to Guides
          </Link>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="container-custom py-20">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-4">
            The article you're looking for doesn't exist.
          </p>
          <Link to="/guides" className="btn-primary">
            Back to Guides
          </Link>
        </div>
      </div>
    );
  }

   const renderMarkdownContent = (content: string) => {
    return (
      <div className="prose prose-lg max-w-none prose-headings:text-gray-800 prose-a:text-gray-800 leading-relaxed">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {content}
        </ReactMarkdown>
      </div>
    );
  };

  return (
    <div className="container-custom">
      <Breadcrumb
        items={[
          { label: 'Home', href: '/' },
          { label: 'Guides', href: '/guides' },
          { label: category?.name || 'Article' },
          { label: article.title }
        ]}
      />

      <article className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
        <header className="bg-gradient-to-br from-primary-600 to-primary-800 text-white p-8">
          {article.featured && (
            <div className="inline-block bg-yellow-500 text-gray-900 text-xs font-bold px-3 py-1 rounded-full mb-4">
              Featured Article
            </div>
          )}
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-primary-100 mb-4">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3M8 7l4-4 4 4M8 7H4v12h16V7z" />
            </svg>
            {article.publishedAt}
            <span className="mx-2">•</span>
            <span className="mx-2">{article.readTime}</span>
            {category && category.icon && (
              <span className="inline-flex items-center">
                <span>{category.icon}</span>
                <span className="ml-1">{category.name}</span>
              </span>
            )}
          </div>
          <div className="p-8">
            {renderMarkdownContent(article.content)}
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex flex-wrap gap-2">
              <span className="text-sm font-medium text-gray-600 mr-2">Tags:</span>
              {article.tags && article.tags.map((tag: string, idx: number) => (
                <Link
                  key={idx}
                  to={`/guides?tag=${tag}`}
                  className="px-3 py-1 bg-primary-100 text-primary-700 text-sm rounded-full hover:bg-primary-200 transition-colors"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </div>
        </header>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link
            to="/services"
            className="bg-gradient-to-br from-primary-600 to-primary-700 text-white rounded-lg p-6 hover:shadow-lg transition-shadow"
          >
            <h3 className="text-xl font-bold mb-2">Need Expert Advice?</h3>
            <p className="text-primary-100 mb-4">
              Get personalized consultation on choosing right hospital and treatment plan.
            </p>
            <span className="inline-flex items-center text-sm font-medium">
              Get Consultation
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </Link>

          <Link
            to="/hospitals"
            className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
          >
            <h3 className="text-xl font-bold mb-2 text-gray-900">Browse Hospitals</h3>
            <p className="text-gray-600 mb-4">
              Explore our comprehensive directory of top-tier Chinese hospitals.
            </p>
            <span className="inline-flex items-center text-sm font-medium text-primary-600">
              View Directory
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </Link>

          {metaData && Array.isArray(metaData.categories) && (
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4">More Articles</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Link
                  key={metaData.categories[0].id}
                  to={`/guides/${metaData.categories[0].id}`}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                >
                  <h3 className="text-lg font-bold mb-2 text-primary-600">
                    {metaData.categories[0].articleIds[0]}. {metaData.categories[0].name}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Article {metaData.categories[0].articleIds[0]} - {metaData.categories[0].name}
                  </p>
                  <span className="text-sm text-primary-600 font-medium">
                    Read Article →
                  </span>
                </Link>
              </div>
            </div>
          )}
        </div>
      </article>
    </div>
  );
};

export default ArticleDetail;
